import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import DurationService from "app/services/DurationService";

import { getDomainsData } from "../../../../search/overview/helpers/helpers";
import { useChannelData } from "../../common/helpers";
import { buildChannelMetrics, buildRowsFromMetrics, computeTotalChanges } from "../tests/helpers";
import type { TrafficData, MarketingChannelsPresetsResult } from "../types";

interface DomainsConfig {
    domainsKeys?: string;
    domainsData?: Record<string, { name: string; url: string; favicon: string }>;
}

const useMarketingChannelsPresets = (
    comparedDuration: string,
    domainsConfig?: DomainsConfig,
): MarketingChannelsPresetsResult => {
    const { domainsKeys, domainsData } = domainsConfig || {};
    const { params } = useCurrentStateAndParams();
    const { duration } = params;
    const durationObject = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration),
        [duration, comparedDuration],
    );

    const { data, isLoading } = useChannelData({
        params,
        durationObject,
        isPeriodOverPeriod: !!comparedDuration,
        domainsKeys,
    });

    const { percentageData, absoluteData, domains } = useMemo(() => {
        const curr = (data as TrafficData)?.Data?.TrafficShare;
        const prev = (data as TrafficData)?.ComparedData?.Data?.TrafficShare;
        if (!curr || !prev) {
            return { percentageData: [], absoluteData: [], domains: [] };
        }

        const { channelMetricsMap, totalMetrics } = buildChannelMetrics(curr, prev);
        const updated = computeTotalChanges(totalMetrics, prev);

        const meta = domainsData ?? getDomainsData();
        const keys = Object.keys(meta);

        return {
            percentageData: buildRowsFromMetrics(updated, channelMetricsMap, "percentage", keys),
            absoluteData: buildRowsFromMetrics(updated, channelMetricsMap, "absolute", keys),
            domains: keys.map((name) => ({ name, favicon: meta[name]?.favicon ?? null })),
        };
    }, [data]);

    return { percentageData, absoluteData, domains, isLoading };
};

export default useMarketingChannelsPresets;
